/**
 * How far past its scheduled length a call can run. Interview agents are built
 * with this as their hard stop, so once it has passed a call cannot still be
 * live.
 */
export const CALL_GRACE_MINUTES = 5;
/**
 * The status to show for an interview right now.
 *
 * `expired` is never written. An invite passes its deadline without anything
 * happening to the row, which still says `invited`; deriving it on read means
 * the recruiter's list and the candidate's page are right the moment the link
 * stops working, rather than whenever a job next sweeps.
 */
export function effectiveStatus(interview, now = Date.now()) {
    return interview.status === 'invited' && Date.parse(interview.expiresAt) <= now
        ? 'expired'
        : interview.status;
}
/**
 * Why a new link cannot be issued for this interview yet, or undefined when it
 * can.
 *
 * Anything that was never finished can have its link reissued: unused,
 * expired, revoked or abandoned. A call still marked in progress can too, but
 * only once it cannot still be running - reissuing a live call would hand the
 * candidate a second attempt in the middle of the first. `until` says when that
 * becomes possible.
 */
export function reissueBlocker(interview, durationMinutes, now = Date.now()) {
    const status = effectiveStatus(interview, now);
    if (status === 'completed' || status === 'scored' || status === 'scoring_failed') {
        return { reason: 'This interview has already been taken, so it cannot be taken again.' };
    }
    if (status === 'in_progress') {
        const startedAt = Date.parse(interview.startedAt ?? interview.updatedAt);
        const endsBy = startedAt + (durationMinutes + CALL_GRACE_MINUTES) * 60_000;
        if (now < endsBy) {
            return { reason: 'This interview may still be under way.', until: new Date(endsBy).toISOString() };
        }
    }
    return undefined;
}
//# sourceMappingURL=lifecycle.js.map